import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

export const ConversationSchema = z.object({
  conversationId: PrimaryKeySchema,
  title: z.string(),
  description: z.string().nullable(),
  createdByUserId: PrimaryKeySchema.nullable(),
  updatedByUserId: PrimaryKeySchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type Conversation = z.infer<typeof ConversationSchema>;

export const ConversationsSchema = z.array(ConversationSchema);

export type Conversations = z.infer<typeof ConversationsSchema>;

export const ConversationsQuerySchema =
  createOffsetPaginationRequestQuerySchema(
    z.enum(['title', 'createdAt', 'updatedAt']),
  ).extend({
    conversationIds: commaDelimitedList(z.string().array()).optional(),
    teamIds: commaDelimitedList(z.string().array()).optional(),
    projectIds: commaDelimitedList(z.string().array()).optional(),
    exploreRequestIds: commaDelimitedList(z.string().array()).optional(),
    researchRequestIds: commaDelimitedList(z.string().array()).optional(),
    titleLike: z.string().optional(),
  });

export type ConversationsQuery = z.infer<typeof ConversationsQuerySchema>;

const BaseCreateConversationSchema = ConversationSchema.pick({
  title: true,
  description: true,
});

export const CreateConversationSchema = z.discriminatedUnion('type', [
  BaseCreateConversationSchema.merge(
    z.object({
      type: z.literal('team'),
      teamId: z.string(),
    }),
  ),
  BaseCreateConversationSchema.merge(
    z.object({
      type: z.literal('project'),
      projectId: z.string(),
    }),
  ),
  BaseCreateConversationSchema.merge(
    z.object({
      type: z.literal('researchRequest'),
      researchRequestId: z.string(),
    }),
  ),
]);

export type CreateConversation = z.infer<typeof CreateConversationSchema>;

export const UpdateConversationSchema = ConversationSchema.pick({
  title: true,
  description: true,
  updatedByUserId: true,
}).partial();

export type UpdateConversation = z.infer<typeof UpdateConversationSchema>;
